import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { base_url } from '../../utils/config'

// 1️⃣ Fetch current remaining credits
export const fetchCredits = createAsyncThunk(
  'credits/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth
      const { data } = await axios.get(
        `${base_url}/api/credits`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // new backend returns { free, purchased, available, expiry }
      return data.available
    } catch (e) {
      return rejectWithValue(e.response?.data)
    }
  }
)

// 2️⃣ Create Razorpay order
export const createOrder = createAsyncThunk(
  'credits/createOrder',
  // we call this with { credits: packSize, amount: priceInPaise }
  async ({ credits, amount }, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth
      const { data } = await axios.post(
        `${base_url}/api/credits/order`,
        { credits, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // backend returns { orderId, key }
      return data
    } catch (e) {
      return rejectWithValue(e.response?.data)
    }
  }
)

// 3️⃣ Verify payment & top up credits
export const verifyPayment = createAsyncThunk(
  'credits/verifyPayment',
  async ({ orderId, razorpayPaymentId, razorpaySignature }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth
      const { data } = await axios.post(
        `${base_url}/api/credits/verify`,
        { razorpay_order_id: orderId,
          razorpay_payment_id: razorpayPaymentId,
          razorpay_signature: razorpaySignature
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // backend returns { free, purchased, expiry }
      const available = data.free + data.purchased
      dispatch(setCredits(available))
      return available
    } catch (e) {
      return rejectWithValue(e.response?.data)
    }
  }
)

const creditsSlice = createSlice({
  name: 'credits',
  initialState: {
    remaining:    0,
    loading:      false,
    error:        null,
    order:        null,    // { orderId, key }
    purchasing:   false,
    purchaseError:null
  },
  reducers: {
    setCredits: (state, action) => {
      state.remaining = action.payload
    }
  },
  extraReducers: builder => {
    builder
      // fetchCredits
      .addCase(fetchCredits.pending, state => {
        state.loading = true; state.error = null
      })
      .addCase(fetchCredits.fulfilled, (state, action) => {
        state.loading   = false
        state.remaining = action.payload
      })
      .addCase(fetchCredits.rejected, (state, action) => {
        state.loading = false
        state.error   = action.payload?.error || 'Could not load credits'
      })

      // createOrder
      .addCase(createOrder.pending, state => {
        state.purchasing    = true
        state.purchaseError = null
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.purchasing = false
        state.order      = action.payload   // { orderId, key }
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.purchasing    = false
        state.purchaseError = action.payload?.error || 'Order creation failed'
      })

      // verifyPayment
      .addCase(verifyPayment.pending, state => {
        state.purchasing    = true
        state.purchaseError = null
      })
      .addCase(verifyPayment.fulfilled, state => {
        state.purchasing = false
        state.order      = null
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.purchasing    = false
        state.purchaseError = action.payload?.error || 'Payment verification failed'
      })
  }
})

export const { setCredits } = creditsSlice.actions
export default creditsSlice.reducer
